<template>
  <div>
    <header
      :id="liquid.sectionId"
      ref="siteHeader"
      class="site-header"
      :class="{
        ...headerClasses,
        [stickyClass]: header.isSticky,
        'is-cart-drawer-open': header.isCartDrawerOpen,
        'is-mobile-menu-open': header.isMobileMenuOpen,
        [hiddenClass]: header.isHidden,
      }"
      @transitionend="handleTransitionEnd"
    >
      <site-header-mobile
        v-if="isMobile || isTablet"
        :logo-object="logoObject"
        :alt-logo-object="altLogoObject"
        :logo-src="logoSrc"
        :alt-logo-src="altLogoSrc"
        :logo-text="logoText"
        :wishlist-url="wishlistLink"

        :enable-header-cart="enableHeaderCart"
        :enable-header-search="enableHeaderSearch"
        :enable-header-wishlist="enableHeaderWishlist"
        :enable-header-account="enableHeaderAccount"
        :enable-mobile-carousel="enableMobileCarousel"
        :enable-link-background="enableLinkBackground"

        :breadcrumb="breadcrumb"
        :mobile-top-carousel="mobileTopCarousel"
        :main-menu="mainMenu"
        :section-blocks="mobileBlocks"
        :is-search-opened="isSearchOpened"
        :liquid="liquid"

        @on-mobile-menu-toggle="handleMobileMenuToggle"
      />
      <site-header-desktop
        v-if="!isMobile && !isTablet"
        :logo-object="logoObject"
        :alt-logo-object="altLogoObject"
        :logo-src="logoSrc"
        :alt-logo-src="altLogoSrc"
        :logo-text="logoText"
        :wishlist-url="wishlistLink"

        :enable-header-cart="enableHeaderCart"
        :enable-header-search="enableHeaderSearch"
        :enable-header-wishlist="enableHeaderWishlist"
        :enable-header-account="enableHeaderAccount"
        :enable-link-background="enableLinkBackground"
        :enable-megamenu="enableMegamenu"

        :main-menu="mainMenu"
        :section-blocks="desktopBlocks"
        :is-search-opened="isSearchOpened"
        :liquid="liquid"
      />
      <search-modal
        v-if="enableHeaderSearch && showLazyLoaded"
        @on-search-toggle="onSearchToggle"
      />
    </header>
  </div>
</template>

<script>

/**
 * Section: Site Header
 * ------------------------------------------------------------------------------
 * @param {string}  [liquid.sectionId] - Section's Liquid ID.
 * @param {boolean} [enableStickyHeader] - Global setting for enabling sticky header.
 * @param {boolean} [enableHeaderAccount] - Setting for enabling header account.
 * @param {boolean} [enableHeaderCart] - Setting for enabling header cart.
 * @param {boolean} [enableHeaderSearch] - Setting for enabling header search.
 * @param {boolean} [enableHeaderWishlist] - Setting for enabling header wishlist.
 * @param {boolean} [enableMobileCarousel] - Setting for enabling header collection menu slider on Mobile Menu.
 * @param {boolean} [enableMegamenu] - Setting for enabling header Megamenu.
 * @param {boolean} [enableLinkBackground] - Setting for enabling background styling for highlighted links.
 * @param {string}  [wishlistUrl] - Link to the Wishlist page.
 * @param {string}  [headerBackdrop] - Setting for the header appearance styles.
 * @param {string}  [stickyHeaderStyle] - Sticky header style either dark or light.
 * @param {object}  [logoObject] - A formatted image logo.
 * @param {object}  [altLogoObject] - A formatted alternative image logo.
 * @param {string}  [logoSrc] - Logo file src with responsive size (max-width from settings).
 * @param {string}  [altLogoSrc] - Alternative logo src with responsive size.
 * @param {string}  [logoText] - Text (SVG) setting for header logo.
 * @param {object}  [breadcrumb] - An object, that contains collection URL and title for breadcrumb that is shown only on PDP.
 * @param {Array}   [mobileTopCarousel] - Formatted nested linklist with collections links and images inside.
 * @param {object}  [mainMenu] - Formatted nested linklist.
 * @param {Array}   [sectionBlocks] - Formatted section blocks array.
 */
import { mapState } from 'pinia'

import { defineAsyncComponent } from 'vue'
import { nextTick } from 'vue'

import { useScreenStore } from '~stores/screen'
import { useLocaleStore } from '~stores/locale'

import { waitForElem } from '~/helpers/dom'
import { debounce } from '~/helpers/utils'
import classes from '~/helpers/classes'

import SiteHeaderMobile from '~components/site-header-components/site-header-mobile/site-header-mobile'
import SiteHeaderDesktop from '~components/site-header-components/site-header-desktop/site-header-desktop'

export default {
  name: 'SiteHeader',

  components: {
    SiteHeaderMobile,
    SiteHeaderDesktop,
    SearchModal: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.search-modal' */
        '~legacy-vue/SearchModal'
      ),
    }),
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    enableStickyHeader: {
      type: Boolean,
      default: true,
    },
    enableHeaderAccount: {
      type: Boolean,
      default: false,
    },
    enableHeaderCart: {
      type: Boolean,
      default: false,
    },
    enableHeaderSearch: {
      type: Boolean,
      default: false,
    },
    enableHeaderWishlist: {
      type: Boolean,
      default: false,
    },
    enableMobileCarousel: {
      type: Boolean,
      default: false,
    },
    enableMegamenu: {
      type: Boolean,
      default: false,
    },
    enableLinkBackground: {
      type: Boolean,
      default: false,
    },
    wishlistUrl: {
      type: [ String ],
      default: '',
    },
    headerBackdrop: {
      type: String,
      default: '',
    },
    stickyHeaderStyle: {
      type: String,
      default: '',
    },
    logoObject: {
      type: [ Boolean, Object ],
      default: false,
    },
    altLogoObject: {
      type: [ Boolean, Object ],
      default: false,
    },
    logoSrc: {
      type: String,
      default: '',
    },
    altLogoSrc: {
      type: String,
      default: '',
    },
    logoText: {
      type: String,
      default: '',
    },
    breadcrumb: {
      type: [ Boolean, Object ],
      default: false,
    },
    mobileTopCarousel: {
      type: Array,
      default: () => [],
    },
    menuLinks: {
      type: Array,
      default: () => [],
    },
    sectionBlocks: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      header: {
        initialHeight: 68,
        height: 68,
        isSticky: false,
        isMobileMenuOpen: false,
        isCartDrawerOpen: false,
        isHidden: false,
      },
      announcement: {
        element: null,
        initialHeight: 32,
        height: 32,
        isSticky: false,
        isHidden: false,
      },
      collectionToolbar: null,
      isSearchOpened: false,
      showLazyLoaded: false,
      stickyOffset: 0,
    }
  },

  computed: {

    /**
     * Map global state.
     */
    ...mapState(useScreenStore, {
      isMobile: 'mobile',
      isTablet: 'tablet',
      viewportWidth: 'width',
    }),

    /**
     * Map locale state.
     */
    ...mapState(useLocaleStore, {
      countryCode: 'countryCode',
    }),

    /**
     * Calculates whether we are on an international locale
     * @returns {boolean}
     */
    isInternationalLocale() {
      return this.countryCode !== 'GB'
    },

    /**
     * Compute sticky class that comes from `classes` object to be able to use
     * it in `template` and `:class` bindings
     * @returns {string}
     */
    stickyClass() {
      return classes.sticky || 'is-sticky'
    },

    /**
     * Compute `hidden` class which differs from the usual `is-hidden`.
     * - It's added for smoother transition when window is scrolled, but element
     * isn't sticky
     * @returns {string}
     */
    hiddenClass() {
      return 'not-visible'
    },

    /**
     * Calculate header style classes based on the settings.
     * @returns {object}
     */
    headerClasses() {
      const headerBackdropClass = this.headerBackdrop

      return {
        'transparent': mint.request.pageType === 'index',
        [headerBackdropClass]: headerBackdropClass,
        [this.stickyHeaderStyle]: this.stickyHeaderStyle,
      }
    },

    /**
     * Calculate section blocks related to the desktop menu.
     * @returns {Array}
     */
    desktopBlocks() {
      return this.sectionBlocks.filter((block) => {
        return block.type.includes('style_') || block.type.includes('alternative_')
      })
    },

    /**
     * Calculate section blocks related to the mobile menu.
     * @returns {Array}
     */
    mobileBlocks() {
      return this.sectionBlocks.filter((block) => {
        return block.type.includes('alternative_') || block.type === 'mobile_images'
      })
    },

    /**
     * Calculate section blocks that replace link text with images.
     * @returns {Array}
     */
    linkedImageBlocks() {
      return this.sectionBlocks.filter((block) => block.type === 'linked_image')
    },

    /**
     * Attach image block data to navigation links.
     * Calculate formatted 'Navigation Badges' blocks.
     * @returns {Array}
     */
    navigationBadgesBlocks() {
      return this.sectionBlocks.filter((block) => block.type === 'badges')
    },

    /**
     * Generate the enriched main navigation menu. Adds additional fields based
     * on nav item titles:
     * - Attaches image block data (from `linked_image` blocks) to top-level links
     * - Attaches badge objects (from `badges` block) to all-levels links
     * @returns {Array<object>}
     */
    mainMenu() {
      return this.menuLinks.map((link) => {
        for (const imageBlock of this.linkedImageBlocks) {
          if (
            !imageBlock.heading ||
            !imageBlock.content ||
            imageBlock.heading !== link.title
          ) {
            continue
          }

          link.image = imageBlock
        }

        this.attachBadges(link)

        return link
      })
    },

    /**
     * Calculates threshold when the sticky state should be added
     * @returns {number}
     */
    stickyThreshold() {
      return (this.header.initialHeight + this.announcement.initialHeight)
    },

    /**
     * Calculates the URL to the wishlist page.
     * @returns {string}
     */
    wishlistLink() {
      return this.wishlistUrl
        ? this.wishlistUrl
        : `${mint.routes.normalisedRoot}pages/wish-list`
    },
  },

  watch: {

    /**
     * Watching the `isSearchOpened` prop. If the value is true and mobile menu
     * is opened as well, then the menu should be closed to show search overlay.
     * @param newValue
     */
    isSearchOpened(newValue) {
      newValue ? this.bodyLock() : this.bodyUnlock()
    },

    /**
     * The user has resized their screen.
     */
    viewportWidth() {
      nextTick(() => {
        this.setInitialHeights()
        this.calculateCurrentHeights()
      })
    },

    /**
     * The `mobile` state has changed (if you switch the iPad orientation etc.)
     * @param newValue
     */
    isMobile(newValue) {
      if (newValue) {
        return
      }

      this.header.isMobileMenuOpen = false
    },
  },

  mounted() {
    this.resetCriticalStyles()
    this.setInitialHeights()
    this.calculateCurrentHeights()
    this.setCollectionToolbar()
    this.setEventListeners()
    this.toggleLazyLoaded()
  },

  methods: {

    /**
     * Recursively attaches badges to a link and its children.
     * @param {object} link - The nav item object
     */
    attachBadges(link) {
      if (!link || !link?.title || !this.navigationBadgesBlocks?.length) {
        return link
      }

      const normalise = (str) => str?.toString().trim().toLowerCase()

      const title = normalise(link.title)

      for (const block of this.navigationBadgesBlocks) {
        for (const { badge, navItems } of block.badges) {
          const match = navItems
            .split(',')
            .map((item) => normalise(item))
            .includes(title)

          if (match) {
            link.badge = badge
            break
          }
        }
      }

      // Recursively process sublinks
      if (Array.isArray(link.sublinks)) {
        for (const sublink of link.sublinks) {
          this.attachBadges(sublink)

          // Recursively process grandlinks inside sublink
          if (Array.isArray(sublink.grandlinks)) {
            for (const grandlink of sublink.grandlinks) {
              this.attachBadges(grandlink)
            }
          }
        }
      }
    },

    /**
     * Function which set global css variables.
     * @param {string} name - Variable name (should start from '--')
     * @param {string} value - Variable value (with units, e.g '100px')
     */
    setGlobalVariable(name, value) {
      document.documentElement.style.setProperty(`${name}`, `${value}`)
    },

    /**
     * Function which sets global css variables based on data values
     */
    setDimensions() {
      this.setGlobalVariable(
        '--announcement-bar-height',
        `${Math.floor(this.announcement.height)}px`,
      )

      this.setGlobalVariable(
        '--header-height',
        `${Math.floor(this.header.height)}px`,
      )

      this.setGlobalVariable(
        '--header-height-total',
        `${Math.floor(this.announcement.height + this.header.height)}px`,
      )
    },

    /**
     * Set the header and announcement initial (non-sticky) height based on
     * their first-loaded/resized `offsetHeight`.
     * - This is done to preserve height values, which can be changed
     * while scrolling and prevent any flicks/glitches when elements become sticky
     * and change height at the same time.
     */
    setInitialHeights() {
      const announcementBanner = document.querySelector('[data-mint-section="announcement-banner"]') ?? null
      const defaultHeight = this.announcement.height

      this.announcement.element = announcementBanner || null
      this.announcement.initialHeight = announcementBanner?.offsetHeight || defaultHeight
      this.announcement.height = announcementBanner ? (announcementBanner.offsetHeight || defaultHeight) : 0

      this.header.initialHeight = this.$refs.siteHeader.offsetHeight || 0
    },

    /**
     * Function which calculates the header and announcement height based on their
     * `offsetHeight`, should also be called when some event (e.g resize) is
     * triggered.
     */
    calculateCurrentHeights() {
      const announcementBanner = this.announcement.element

      this.header.height = this.$refs.siteHeader.offsetHeight || 0

      if (announcementBanner) {
        this.announcement.height = announcementBanner.offsetHeight || 0

        if (window.scrollY > this.stickyThreshold) {
          this.announcement.height = 0
        }
      }

      this.setDimensions()
    },

    /**
     * If the sticky announcement enabled, add the sticky class to the banners
     */
    setScrollState() {
      this.collectionToolbar?.classList.add(classes.sticky)

      this.enableStickyHeader
        ? this.header.isSticky = true
        : this.header.isHidden = true
    },

    /**
     * If the sticky announcement enabled, remove the sticky class from the banners
     */
    removeScrollState() {
      this.header.isHidden = false
      this.announcement.isHidden = false
      this.header.isSticky = false
      this.announcement.isSticky = false
      this.collectionToolbar?.classList.remove(classes.sticky)
    },

    /**
     * Handles opening hamburger menu
     * @param {boolean} isOpened - emitted value that indicates hamburger open state
     */
    handleMobileMenuToggle(isOpened) {
      isOpened
        ? this.bodyLock()
        : this.bodyUnlock()

      mint.EventBus.dispatch('cartDrawer:close')
      this.header.isMobileMenuOpen = isOpened
      this.calculateCurrentHeights()
    },

    /**
     * Set Event listeners.
     */
    setEventListeners() {
      mint.EventBus.on('cartDrawer:open', () => {
        this.header.isCartDrawerOpen = true
      })

      mint.EventBus.on('cartDrawer:close', () => {
        this.header.isCartDrawerOpen = false
      })

      /**
       * Tracks whether the user has scrolled past the sticky threshold.
       * @type {boolean}
       */
      let hasScrolledPastThreshold = false

      /**
       * Scroll event listener that handles state transitions based on the scroll position.
       * - Transitions to "sticky state" when the user scrolls past the threshold.
       * - Transitions back to "default state" when the user scrolls back above the threshold.
       * - Debounces the execution to improve performance.
       */
      window.addEventListener(
        'scroll',
        debounce(() => {
          const isPastThreshold = window.scrollY > this.stickyThreshold

          if (isPastThreshold && !hasScrolledPastThreshold) {

            // Transition to sticky state
            hasScrolledPastThreshold = true
            this.setScrollState()
            this.calculateCurrentHeights()
          }
          else if (!isPastThreshold && hasScrolledPastThreshold) {

            // Transition back to default state
            hasScrolledPastThreshold = false
            this.calculateCurrentHeights()
          }

          if (document.body.style.overflow !== 'hidden' && window.scrollY <= this.stickyThreshold / 2) {
            this.removeScrollState()
          }
        }, 10, false),
        { passive: true },
      )
    },

    /**
     * Make body scroll totally locked, including the iOS devices.
     * - Using when mobile menu is opened etc.
     * - Note: any CSS solution other than a `fixed` position on body still works
     * incorrectly on iOS.
     */
    bodyLock() {
      document.body.classList.add(classes.locked)
    },

    /**
     * Unblock body to be able to continue scrolling.
     */
    bodyUnlock() {
      document.body.classList.remove(classes.locked)
    },

    /**
     * A method that is called when the search modal is toggled.
     * @param {boolean} isSearchOpened - Emitted value, which indicates the
     * current state of search overlay
     */
    onSearchToggle(isSearchOpened) {
      isSearchOpened
        ? this.bodyLock()
        : this.bodyUnlock()

      this.isSearchOpened = isSearchOpened
    },

    /**
     * Load async components after initial load.
     * - Currently renders only `<search-modal>` component. Since the clicks
     * are handled inside this component, we can't change `v-if` condition on
     * click to render it asynchronously, as we are waiting for the event emits.
     * So we just postpone the loading for now.
     */
    toggleLazyLoaded() {
      setTimeout(() => {
        this.showLazyLoaded = true
      }, 1000)
    },

    /**
     * Change data `collectionToolbar` with DOM node if we are on the
     * collection page.
     */
    setCollectionToolbar() {
      if (mint.request.pageType !== 'collection') {
        return
      }

      this.collectionToolbar = document.querySelector('.toolbar')
    },

    /**
     * Sets the offset once when the header/announcement becomes sticky.
     * - this is done to avoid repeating recalculations every time you scroll,
     * since they are visible due to the transition when there are another
     * sticky elements on the page
     */
    setStickyOffset() {
      this.stickyOffset = 0

      /* Nothing to calculate in non-sticky states */
      if (window.scrollY < this.stickyThreshold) {
        return
      }

      this.stickyOffset += this.announcement.isSticky ? this.announcement.height : 0
      this.stickyOffset += this.header.isSticky ? this.header.height : 0

      this.setGlobalVariable(
        '--header-sticky-offset',
        `${this.stickyOffset}px`,
      )
    },

    /**
     * Used when the site-header is loaded.
     * - Since on the parent section `data-mint-section-state='active'` is
     * triggered before the section is mounted, content shifts are still exist
     * - Therefore, need to add a class to reset them only when the section is
     * fully loaded
     */
    resetCriticalStyles() {
      const parentSection = this.$refs.siteHeader.closest(
        '[data-mint-section="site-header"]',
      )

      parentSection?.classList.add('is-mounted')
    },

    /**
     * Wait until the transition is over and recalculate the heights.
     * - this is done because the `setTimeout` worked inconsistently
     * - also to reduce delays between calculations without significantly
     * impacting performance (if to do it on 'scroll' event)
     * @param {Event} event - transition HTML Event.
     */
    handleTransitionEnd(event) {
      const affectedProperties = [
        'height', 'max-height', 'min-height', 'padding', 'padding-top', 'padding-bottom', 'transform', 'flex-basis',
      ]

      if (!affectedProperties.includes(event?.propertyName)) {
        return
      }

      this.calculateCurrentHeights()
      this.setStickyOffset()
    },
  },
}
</script>

<style src="./site-header.scss" lang="scss"></style>
